import React from 'react'

function ToggleSwitch({ checked, disabled }) {
  return (
    <span
      aria-hidden="true"
      className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition ${
        checked ? 'bg-orange-500' : 'bg-slate-200'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transition ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </span>
  )
}

export function EasyReconfigureContent({
  title,
  description,
  purposeLabel = '목적을 골라주세요',
  purposes = [],
  purposeId,
  onPurposeIdChange,
  features = [],
  featureValues = {},
  onFeatureChange,
  onApply,
  onClose,
  applyLabel = '구성 적용',
  note,
  stepMode = false,
  stepLabels = ['목적 선택', '기능 선택'],
  renderReview,
  reviewLabel = '작성 항목',
  renderPurposePreview,
  renderPurposeAction,
  purposeGridClassName,
  onCancel,
  cancelLabel = '취소',
  className = '',
  showCloseButton = true,
}) {
  const [stepIndex, setStepIndex] = React.useState(0)
  const hasPurposes = purposes.length > 0
  const hasFeatures = features.length > 0
  const hasReview = typeof renderReview === 'function'
  const useStepFlow = Boolean(stepMode && hasPurposes && hasFeatures)
  const stepCount = useStepFlow ? (hasReview ? 3 : 2) : 0
  const visibleStepLabels = useStepFlow
    ? [...stepLabels, reviewLabel].slice(0, stepCount)
    : []

  React.useEffect(() => {
    setStepIndex(0)
  }, [purposeId])

  const showPurposes = !useStepFlow || stepIndex === 0
  const showFeatures = !useStepFlow || stepIndex === 1
  const showReview = useStepFlow && hasReview && stepIndex === 2
  const primaryLabel = useStepFlow && stepIndex < stepCount - 1 ? '다음' : applyLabel
  const onPrimaryClick = () => {
    if (useStepFlow && stepIndex < stepCount - 1) {
      setStepIndex((idx) => Math.min(stepCount - 1, idx + 1))
      return
    }
    onApply?.()
  }

  return (
    <div className={`flex max-h-[calc(100vh-3rem)] w-full flex-col overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-black/10 sm:max-h-[92vh] ${className}`}>
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-black text-slate-950">{title}</h2>
            {description ? (
              <p className="mt-1 text-sm leading-6 text-slate-500">{description}</p>
            ) : null}
          </div>
          {showCloseButton ? (
            <button
              type="button"
              className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              onClick={onClose}
            >
              닫기
            </button>
          ) : null}
        </div>

        <div className="min-h-0 overflow-y-auto px-5 py-5">
          {useStepFlow ? (
            <div className={`mb-5 grid gap-2 ${stepCount === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
              {visibleStepLabels.map((label, index) => {
                const active = index === stepIndex
                const done = index < stepIndex
                return (
                  <button
                    key={`${label}-${index}`}
                    type="button"
                    className={`flex items-center gap-2 rounded-2xl border px-3 py-2 text-left text-xs font-bold transition ${
                      active
                        ? 'border-brand-navy bg-sky-50 text-brand-navy'
                        : done
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-slate-200 bg-white text-slate-400'
                    }`}
                    onClick={() => {
                      if (index <= stepIndex) setStepIndex(index)
                    }}
                    aria-current={active ? 'step' : undefined}
                  >
                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] ${
                      active
                        ? 'bg-brand-navy text-white'
                        : done
                          ? 'bg-emerald-600 text-white'
                          : 'bg-slate-100 text-slate-400'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="min-w-0 truncate">{label}</span>
                  </button>
                )
              })}
            </div>
          ) : null}

          {hasPurposes && showPurposes ? (
            <section>
              <div className="text-sm font-bold text-slate-900">{purposeLabel}</div>
              <div className={purposeGridClassName || 'mt-3 grid gap-2 sm:grid-cols-2'}>
                {purposes.map((purpose) => {
                  const active = purpose.id === purposeId
                  const preview = renderPurposePreview?.(purpose)
                  const action = renderPurposeAction?.(purpose)
                  const purposeClassName = `rounded-2xl border px-4 py-3 text-left transition ${
                    active
                      ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`
                  const content = (
                    <>
                      {preview ? (
                        <div className="mb-3">{preview}</div>
                      ) : null}
                      <span className="block text-sm font-extrabold">{purpose.label}</span>
                      {purpose.description ? (
                        <span className="mt-1 block text-xs leading-5 text-slate-500">
                          {purpose.description}
                        </span>
                      ) : null}
                      {action ? (
                        <div className="mt-3">{action}</div>
                      ) : null}
                    </>
                  )

                  if (preview || action) {
                    return (
                      <div
                        key={purpose.id}
                        role="button"
                        tabIndex={0}
                        className={purposeClassName}
                        onClick={() => onPurposeIdChange?.(purpose.id)}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return
                          event.preventDefault()
                          onPurposeIdChange?.(purpose.id)
                        }}
                      >
                        {content}
                      </div>
                    )
                  }

                  return (
                    <button
                      key={purpose.id}
                      type="button"
                      className={purposeClassName}
                      onClick={() => onPurposeIdChange?.(purpose.id)}
                    >
                      {content}
                    </button>
                  )
                })}
              </div>
            </section>
          ) : null}

          {hasFeatures && showFeatures ? (
            <section className={hasPurposes && !useStepFlow ? 'mt-6' : ''}>
              <div className="text-sm font-bold text-slate-900">보여줄 기능을 골라주세요</div>
              <div className="mt-3 overflow-hidden rounded-3xl border border-slate-200 bg-white">
                {features.map((feature, index) => {
                  const required = Boolean(feature.required)
                  const checked = required || featureValues[feature.id] === true
                  return (
                    <div
                      key={feature.id}
                      className={`flex items-center gap-4 px-4 py-4 ${
                        index === 0 ? '' : 'border-t border-slate-100'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-base font-extrabold text-slate-950">
                            {feature.label}
                          </div>
                          {required ? (
                            <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-white">
                              기본 포함
                            </span>
                          ) : feature.recommended ? (
                            <span className="rounded-full bg-orange-50 px-2.5 py-1 text-[11px] font-bold text-orange-700">
                              추천
                            </span>
                          ) : null}
                        </div>
                        {feature.description ? (
                          <div className="mt-1 text-sm leading-6 text-slate-500">
                            {feature.description}
                          </div>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={checked}
                        aria-label={`${feature.label} ${checked ? '켜짐' : '꺼짐'}`}
                        disabled={required}
                        className="shrink-0 disabled:cursor-default"
                        onClick={() => {
                          if (required) return
                          onFeatureChange?.(feature.id, !checked)
                        }}
                      >
                        <ToggleSwitch checked={checked} disabled={required} />
                      </button>
                    </div>
                  )
                })}
              </div>
            </section>
          ) : null}

          {showReview ? renderReview({
            features,
            featureValues,
            purposes,
            purposeId,
          }) : null}

          {note && (!useStepFlow || stepIndex === stepCount - 1) ? (
            <p className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-600">
              {note}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4">
          {onCancel ? (
            <button
              type="button"
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={onCancel}
            >
              {cancelLabel}
            </button>
          ) : null}
          {useStepFlow && stepIndex > 0 ? (
            <button
              type="button"
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => setStepIndex((idx) => Math.max(0, idx - 1))}
            >
              이전
            </button>
          ) : null}
          <button
            type="button"
            className="rounded-xl bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            onClick={onPrimaryClick}
          >
            {primaryLabel}
          </button>
        </div>
    </div>
  )
}

export default function EasyReconfigureModal({
  open,
  maxWidthClassName = 'max-w-2xl',
  onClose,
  ...props
}) {
  React.useEffect(() => {
    if (!open) return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose, open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center overflow-hidden bg-slate-950/55 px-4 py-6">
      <div className={`flex max-h-[calc(100vh-3rem)] w-full sm:max-h-[92vh] ${maxWidthClassName}`}>
        <EasyReconfigureContent
          {...props}
          onClose={onClose}
          onCancel={onClose}
        />
      </div>
    </div>
  )
}
